import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, EmptyState, Chip, StatCard } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    Plus, PencilSimple, Trash, Receipt, CheckCircle, FileCsv, FilePdf, DownloadSimple, Star, Users as UsersIcon, IdentificationCard, Car
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { UTILITIES, UTIL_MAP } from "@/lib/utilityTypes";
import { APPLIANCE_TYPE_MAP } from "@/lib/applianceTypes";
import { PARKING_TYPE_MAP, VEHICLE_TYPE_MAP, APPROVAL_STATUS_LABELS, APPROVAL_STATUS_VARIANTS } from "@/lib/parkingTypes";
import AddApplianceDialog from "@/components/AddApplianceDialog";
import VehicleDialog from "@/components/VehicleDialog";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export default function MyFlat() {
    const { user } = useAuth();
    const [data, setData] = useState(null);

    // Bill dialog
    const [billOpen, setBillOpen] = useState(false);
    const [billForm, setBillForm] = useState({ utility_type: "electricity", connection_id: "", amount: "", bill_period: "", due_date: "", provider_name: "", customer_id: "", notes: "" });

    // Utility connection dialog
    const [connOpen, setConnOpen] = useState(false);
    const [editingConn, setEditingConn] = useState(null);
    const [billers, setBillers] = useState([]);
    const [connForm, setConnForm] = useState({
        utility_type: "electricity", biller_id: "", provider_name: "", customer_id: "",
        nickname: "", meter_number: "", notes: "",
    });

    // Appliances
    const [appliances, setAppliances] = useState([]);
    const [applianceOpen, setApplianceOpen] = useState(false);
    const [editingAppliance, setEditingAppliance] = useState(null);

    async function loadAppliances(fid) {
        try {
            const { data: d } = await api.get(`/flats/${fid}/appliances`);
            setAppliances(d || []);
        } catch (e) { toast.error(formatError(e)); }
    }
    function openAddAppliance() { setEditingAppliance(null); setApplianceOpen(true); }
    function openEditAppliance(a) { setEditingAppliance(a); setApplianceOpen(true); }
    async function removeAppliance(a) {
        if (!window.confirm(`Remove ${a.brand || ""} ${a.model || APPLIANCE_TYPE_MAP[a.appliance_type]?.label || "appliance"}?`)) return;
        try { await api.delete(`/appliances/${a.id}`); toast.success("Appliance removed"); loadAppliances(a.flat_id); }
        catch (e) { toast.error(formatError(e)); }
    }

    // Parking & Vehicles
    const [parkingSlots, setParkingSlots] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [vehicleOpen, setVehicleOpen] = useState(false);
    const [editingVehicle, setEditingVehicle] = useState(null);

    async function loadParking(fid) {
        try {
            const { data: d } = await api.get(`/flats/${fid}/parking`);
            setParkingSlots(d || []);
        } catch (e) { toast.error(formatError(e)); }
    }
    async function loadVehicles(fid) {
        try {
            const { data: d } = await api.get(`/flats/${fid}/parking-vehicles`);
            setVehicles(d || []);
        } catch (e) { toast.error(formatError(e)); }
    }
    function openAddVehicle() { setEditingVehicle(null); setVehicleOpen(true); }
    function openEditVehicle(v) { setEditingVehicle(v); setVehicleOpen(true); }
    async function deactivateVehicle(v) {
        if (!window.confirm(`Deactivate ${v.registration_number}? It will be removed from any assigned parking slot.`)) return;
        try {
            await api.patch(`/parking-vehicles/${v.id}`, { is_active: false });
            toast.success("Vehicle deactivated");
            loadVehicles(v.flat_id); loadParking(v.flat_id);
        } catch (e) { toast.error(formatError(e)); }
    }
    async function assignVehicleToSlot(vehicleId, slotId) {
        try {
            await api.post(`/parking-vehicles/${vehicleId}/assign-slot`, { parking_slot_id: slotId || null });
            toast.success(slotId ? "Vehicle assigned to parking slot" : "Vehicle unassigned");
            loadParking(data.flat.id);
        } catch (e) { toast.error(formatError(e)); }
    }

    async function load() {
        try {
            const { data: d } = await api.get("/my-flat");
            setData(d);
            if (d.flat?.id) { loadAppliances(d.flat.id); loadParking(d.flat.id); loadVehicles(d.flat.id); }
        } catch (e) { toast.error(formatError(e)); }
    }
    async function loadBillers() {
        try {
            const { data: d } = await api.get("/utility-billers");
            setBillers(d || []);
        } catch (e) { toast.error(formatError(e)); }
    }
    useEffect(() => { load(); loadBillers(); }, []);

    if (!data) return <div className="p-6 text-brand-inkSoft text-sm">Loading your flat…</div>;
    if (!data.flat) {
        return (
            <div className="animate-fade-up">
                <PageHeader overline="Your home" title="My flat" />
                <EmptyState
                    title="You're not assigned to a flat yet"
                    description="Please contact your committee to assign your flat. Once assigned, you can track your utility connections and bills here."
                />
            </div>
        );
    }

    const flat = data.flat;
    const bills = data.recent_bills || [];
    const conns = data.connections || [];
    const unpaid = bills.filter((b) => b.status !== "paid");
    const pendingAmount = unpaid.reduce((s, b) => s + Number(b.amount || 0), 0);

    const connsByType = {};
    for (const c of conns) (connsByType[c.utility_type] = connsByType[c.utility_type] || []).push(c);
    const billersByType = {};
    for (const b of billers) (billersByType[b.utility_type] = billersByType[b.utility_type] || []).push(b);
    const billerOptions = billersByType[connForm.utility_type] || [];
    const selectedBiller = billerOptions.find((b) => b.id === connForm.biller_id);

    // ---- Connection actions ----
    function openCreateConn(utilityType) {
        setEditingConn(null);
        setConnForm({ utility_type: utilityType, biller_id: "", provider_name: "", customer_id: "", nickname: "", meter_number: "", notes: "" });
        setConnOpen(true);
    }
    function openEditConn(c) {
        setEditingConn(c);
        setConnForm({
            utility_type: c.utility_type,
            biller_id: c.biller_id || "",
            provider_name: c.provider_name || "",
            customer_id: c.customer_id || "",
            nickname: c.nickname || "",
            meter_number: c.meter_number || "",
            notes: c.notes || "",
        });
        setConnOpen(true);
    }
    async function saveConn(e) {
        e.preventDefault();
        try {
            const payload = { ...connForm };
            Object.keys(payload).forEach((k) => { if (payload[k] === "") payload[k] = null; });
            if (editingConn) {
                await api.patch(`/utility-connections/${editingConn.id}`, payload);
                toast.success("Connection updated");
            } else {
                await api.post(`/flats/${flat.id}/utility-connections`, payload);
                toast.success("Connection added");
            }
            setConnOpen(false);
            load();
        } catch (e) { toast.error(formatError(e)); }
    }
    async function removeConn(c) {
        if (!confirm(`Remove ${UTIL_MAP[c.utility_type]?.label || c.utility_type} connection?`)) return;
        try { await api.delete(`/utility-connections/${c.id}`); toast.success("Removed"); load(); }
        catch (e) { toast.error(formatError(e)); }
    }

    // ---- Bill actions ----
    function openCreateBill(prefill = {}) {
        setBillForm({
            utility_type: prefill.utility_type || "electricity",
            connection_id: prefill.connection_id || "",
            provider_name: prefill.provider_name || "",
            customer_id: prefill.customer_id || "",
            amount: "",
            bill_period: "",
            due_date: "",
            notes: "",
        });
        setBillOpen(true);
    }
    async function saveBill(e) {
        e.preventDefault();
        try {
            const payload = {
                flat_id: flat.id,
                utility_type: billForm.utility_type,
                connection_id: billForm.connection_id || null,
                provider_name: billForm.provider_name || null,
                customer_id: billForm.customer_id || null,
                amount: Number(billForm.amount),
                bill_period: billForm.bill_period,
                due_date: billForm.due_date,
                notes: billForm.notes || null,
            };
            await api.post("/utility-bills", payload);
            toast.success("Bill added");
            setBillOpen(false);
            load();
        } catch (e) { toast.error(formatError(e)); }
    }
    async function payBill(b) {
        try { await api.post(`/utility-bills/${b.id}/pay`, { method: "manual" }); toast.success("Marked paid"); load(); }
        catch (e) { toast.error(formatError(e)); }
    }
    async function removeBill(b) {
        if (!confirm("Delete this bill?")) return;
        try { await api.delete(`/utility-bills/${b.id}`); toast.success("Deleted"); load(); }
        catch (e) { toast.error(formatError(e)); }
    }

    // When picking a connection in bill dialog, prefill provider/customer
    function onPickConnection(cid) {
        const c = conns.find((x) => x.id === cid);
        setBillForm((f) => ({
            ...f,
            connection_id: cid,
            provider_name: c?.provider_name || f.provider_name,
            customer_id: c?.customer_id || f.customer_id,
            utility_type: c?.utility_type || f.utility_type,
        }));
    }

    // Payment history download functions
    async function downloadCSV() {
        try {
            const response = await api.get("/my-flat/payment-history/csv", { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `payment-history-${flat.block}-${flat.number}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("CSV downloaded!");
        } catch (e) { toast.error(formatError(e)); }
    }

    async function downloadPDF() {
        try {
            const response = await api.get("/my-flat/payment-history/pdf", { responseType: 'blob' });
            const url = window.URL.createObjectURL(new Blob([response.data], { type: 'text/html' }));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `payment-history-${flat.block}-${flat.number}.html`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Payment history downloaded! You can print it as PDF from your browser.");
        } catch (e) { toast.error(formatError(e)); }
    }

    return (
        <div className="animate-fade-up">
            <PageHeader
                overline="Your home"
                title={`Flat ${flat.block}-${flat.number}`}
                description={`${flat.floor ? `Floor ${flat.floor} · ` : ""}${flat.bhk || ""} · ${flat.occupancy}-occupied`}
                actions={
                    <div className="flex gap-2">
                        <Button data-testid="add-utility-bill-btn" onClick={() => openCreateBill()}
                            className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white active:scale-[0.98]">
                            <Receipt size={16} className="mr-1.5" /> Log utility bill
                        </Button>
                    </div>
                }
            />

            {/* Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                <StatCard label="Residents" value={data.residents.length} />
                <StatCard label="Connections" value={conns.length} hint={`${new Set(conns.map(c => c.utility_type)).size} utility type(s)`} />
                <StatCard label="Bills tracked" value={bills.length} />
                <StatCard label="Pending" value={inr(pendingAmount)} hint={`${unpaid.length} unpaid`} accent />
            </div>

            {/* My Household — read-only view of who's linked to this flat */}
            {data.household && (
                <div className="mb-10 bg-white border border-brand-line rounded-sm p-6" data-testid="my-household-section">
                    <div className="flex items-center gap-2 mb-4">
                        <UsersIcon size={18} className="text-brand-ink" />
                        <h2 className="font-heading text-xl text-brand-ink">My Household</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {["owner", "tenant"].map((key) => (
                            (data.household.active[key] || []).map((r) => (
                                <div key={r.relationship_id} className="border border-brand-line rounded-sm p-3" data-testid={`my-household-member-${r.relationship_id}`}>
                                    <p className="text-sm font-medium text-brand-ink flex items-center gap-1.5">
                                        {r.name}
                                        {r.is_primary_contact && <Star size={11} className="text-brand-action" weight="fill" />}
                                    </p>
                                    <p className="text-xs text-brand-inkSoft mt-0.5">{r.relationship_label}</p>
                                </div>
                            ))
                        ))}
                        {[...(data.household.active.family || []), ...(data.household.active.other || [])].map((r) => (
                            <div key={r.relationship_id} className="border border-brand-line rounded-sm p-3" data-testid={`my-household-member-${r.relationship_id}`}>
                                <p className="text-sm font-medium text-brand-ink flex items-center gap-1.5">
                                    {r.name}
                                    {r.is_primary_contact && <Star size={11} className="text-brand-action" weight="fill" />}
                                </p>
                                <p className="text-xs text-brand-inkSoft mt-0.5">{r.relationship_label}</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Payment History Export */}
            <div className="mb-10 bg-white border border-brand-line rounded-sm p-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="font-heading text-xl text-brand-ink">Payment History</h2>
                        <p className="text-sm text-brand-inkSoft mt-1">Download your complete maintenance payment records</p>
                    </div>
                    <div className="flex gap-2">
                        <Button 
                            onClick={downloadCSV}
                            variant="outline"
                            className="rounded-full border-brand-ink text-brand-ink hover:bg-brand-ink hover:text-white"
                            data-testid="download-csv-btn"
                        >
                            <FileCsv size={16} className="mr-1.5" weight="duotone" /> Download CSV
                        </Button>
                        <Button 
                            onClick={downloadPDF}
                            variant="outline"
                            className="rounded-full border-brand-action text-brand-action hover:bg-brand-action hover:text-white"
                            data-testid="download-pdf-btn"
                        >
                            <FilePdf size={16} className="mr-1.5" weight="duotone" /> Download PDF
                        </Button>
                    </div>
                </div>
                <div className="mt-4 flex items-center gap-2 text-xs text-brand-inkSoft">
                    <DownloadSimple size={14} weight="duotone" />
                    <span>Includes all paid maintenance invoices with receipt numbers, dates, amounts, and late fees</span>
                </div>
            </div>

            {/* Utility connections */}
            <div className="mb-10">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="font-heading text-xl text-brand-ink">Utility connections</h2>
                        <p className="text-sm text-brand-inkSoft">Save provider + customer ID once, then log bills in seconds.</p>
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {UTILITIES.filter(u => u.key !== "other").map((u) => {
                        const list = connsByType[u.key] || [];
                        const Icon = u.icon;
                        return (
                            <div key={u.key} data-testid={`utility-slot-${u.key}`}
                                className="bg-white border border-brand-line rounded-sm p-5 hover:-translate-y-1 hover:shadow-lg transition-transform duration-200">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ background: u.color + "22", color: u.color }}>
                                        <Icon size={20} weight="duotone" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-heading text-lg text-brand-ink">{u.label}</p>
                                        <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">
                                            {list.length ? `${list.length} account${list.length > 1 ? "s" : ""}` : "Not configured"}
                                        </p>
                                    </div>
                                </div>
                                <div className="mt-3 space-y-2">
                                    {list.map((c) => (
                                        <div key={c.id} data-testid={`connection-${c.id}`}
                                            className="border border-brand-line rounded-sm p-3 text-sm">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <p className="font-medium text-brand-ink truncate">
                                                        {c.nickname ? `${c.nickname} · ` : ""}{c.provider_name}
                                                    </p>
                                                    <p className="text-xs text-brand-inkSoft mt-0.5 font-mono">
                                                        {c.customer_id_label || "Customer ID"}: {c.customer_id_masked || c.customer_id}
                                                    </p>
                                                    {c.meter_number && <p className="text-xs text-brand-inkSoft mt-0.5">Meter: {c.meter_number}</p>}
                                                    <span data-testid={`connection-status-${c.id}`}
                                                        className="inline-block mt-1.5 text-[10px] uppercase tracking-overline text-brand-inkSoft bg-brand-bg border border-brand-line rounded-full px-2 py-0.5">
                                                        {c.integration_status === "READY" ? "Ready to connect" : "Not connected yet"}
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-1 shrink-0">
                                                    <button data-testid={`edit-connection-${c.id}`} onClick={() => openEditConn(c)}
                                                        className="p-1.5 hover:bg-brand-sage rounded-sm"><PencilSimple size={13} /></button>
                                                    <button data-testid={`delete-connection-${c.id}`} onClick={() => removeConn(c)}
                                                        className="p-1.5 hover:bg-[#FEF2F2] rounded-sm text-brand-action"><Trash size={13} /></button>
                                                </div>
                                            </div>
                                            <button data-testid={`log-bill-${c.id}`}
                                                onClick={() => openCreateBill({ utility_type: c.utility_type, connection_id: c.id, provider_name: c.provider_name, customer_id: c.customer_id })}
                                                className="mt-3 text-xs text-brand-action hover:underline inline-flex items-center gap-1">
                                                <Receipt size={12} /> Log bill for this
                                            </button>
                                        </div>
                                    ))}
                                    <Button data-testid={`add-connection-${u.key}`} onClick={() => openCreateConn(u.key)}
                                        variant="outline" size="sm"
                                        className="w-full rounded-full border-dashed border-brand-line text-brand-inkSoft hover:text-brand-ink hover:border-brand-ink">
                                        <Plus size={14} className="mr-1" /> Add {u.label.toLowerCase()}
                                    </Button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Parking & Vehicles */}
            <div className="mb-10">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="font-heading text-xl text-brand-ink">Parking</h2>
                        <p className="text-sm text-brand-inkSoft">View your allotted parking, assign a vehicle, and see your parking pass.</p>
                    </div>
                </div>
                {parkingSlots.length === 0 ? (
                    <EmptyState title="No parking has been allotted to this flat." />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        {parkingSlots.map((p) => {
                            const t = PARKING_TYPE_MAP[p.parking_type];
                            const Icon = t?.icon;
                            return (
                                <div key={p.allocation_id} data-testid={`parking-slot-${p.allocation_id}`} className="bg-white border border-brand-line rounded-sm p-4">
                                    <div className="flex items-center justify-between">
                                        <p className="text-sm font-medium text-brand-ink flex items-center gap-2">
                                            {Icon && <Icon size={16} weight="duotone" />} {p.slot_number}
                                        </p>
                                        <Chip variant={p.charging_enabled ? "warn" : "success"}>{p.charging_enabled ? "Paid" : "Free"}</Chip>
                                    </div>
                                    <p className="text-xs text-brand-inkSoft mt-1">
                                        {[t?.label, p.block, p.floor && `Floor ${p.floor}`].filter(Boolean).join(" · ") || "Location not set"}
                                    </p>
                                    {p.charging_enabled && (
                                        <p className="text-xs text-brand-inkSoft mt-1">₹{p.charge_amount} / {p.frequency?.replace("_", " ")}</p>
                                    )}
                                    {p.charging_enabled && (
                                        p.latest_invoice ? (
                                            <Link to={`/app/invoices/${p.latest_invoice.id}`} data-testid={`parking-invoice-link-${p.allocation_id}`}
                                                className="text-xs mt-1 inline-flex items-center gap-1.5 text-brand-action hover:underline">
                                                {p.latest_invoice.month} invoice · {inr(p.latest_invoice.amount)}
                                                <Chip variant={p.latest_invoice.status === "paid" ? "success" : "warn"}>{p.latest_invoice.status}</Chip>
                                            </Link>
                                        ) : (
                                            <p className="text-xs text-brand-inkSoft mt-1">No parking invoice raised yet.</p>
                                        )
                                    )}
                                    <div className="mt-3">
                                        <Label className="text-xs">Vehicle</Label>
                                        <Select value={p.vehicle?.id || "__none"} onValueChange={(v) => assignVehicleToSlot(v === "__none" ? p.vehicle?.id : v, v === "__none" ? null : p.slot_id)}
                                            disabled={vehicles.filter((v) => v.is_active !== false && (v.approval_status || "approved") === "approved").length === 0}>
                                            <SelectTrigger data-testid={`parking-vehicle-select-${p.allocation_id}`} className="rounded-sm border-brand-line mt-1"><SelectValue placeholder="No vehicle assigned" /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="__none">No vehicle assigned</SelectItem>
                                                {vehicles.filter((v) => v.is_active !== false && (v.approval_status || "approved") === "approved").map((v) => <SelectItem key={v.id} value={v.id}>{v.registration_number} ({VEHICLE_TYPE_MAP[v.vehicle_type]?.label})</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        {vehicles.filter((v) => v.is_active !== false && (v.approval_status || "approved") !== "approved").length > 0 && (
                                            <p className="text-xs text-brand-inkSoft mt-1">Some of your vehicles are awaiting approval and aren't listed here yet.</p>
                                        )}
                                    </div>
                                    {p.pass ? (
                                        <div className="mt-3 border border-brand-line rounded-sm p-3 bg-brand-bg" data-testid={`parking-pass-${p.pass.id}`}>
                                            <div className="flex items-center justify-between">
                                                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">Parking Pass</p>
                                                <Chip variant={p.pass.status === "active" ? "success" : p.pass.status === "expired" ? "warn" : "danger"}>{p.pass.status}</Chip>
                                            </div>
                                            <p className="text-sm text-brand-ink font-medium mt-0.5 flex items-center gap-1.5"><IdentificationCard size={14} weight="duotone" /> {p.pass.pass_number}</p>
                                            {p.pass.vehicle_registration_number && (
                                                <p className="text-xs text-brand-inkSoft mt-0.5 font-mono">{p.pass.vehicle_registration_number}{p.pass.sticker_number ? ` · Sticker ${p.pass.sticker_number}` : ""}</p>
                                            )}
                                            <p className="text-xs text-brand-inkSoft mt-0.5">Issued {p.pass.issue_date}{p.pass.expiry_date ? ` · Expires ${p.pass.expiry_date}` : ""}</p>
                                        </div>
                                    ) : <p className="text-xs text-brand-inkSoft mt-3">No parking pass issued yet.</p>}
                                </div>
                            );
                        })}
                    </div>
                )}

                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading text-lg text-brand-ink">My Vehicles</h3>
                    <Button data-testid="add-vehicle-btn" onClick={openAddVehicle} size="sm" className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                        <Plus size={14} className="mr-1" /> Add vehicle
                    </Button>
                </div>
                {vehicles.filter((v) => v.is_active !== false).length === 0 ? (
                    <p className="text-sm text-brand-inkSoft">No vehicles added yet.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {vehicles.filter((v) => v.is_active !== false).map((v) => {
                            const t = VEHICLE_TYPE_MAP[v.vehicle_type];
                            const Icon = t?.icon || Car;
                            const isOwnOrUnclaimed = !v.resident_id || v.resident_id === user?.id;
                            const approvalStatus = v.approval_status || "approved";
                            return (
                                <div key={v.id} data-testid={`vehicle-card-${v.id}`} className="bg-white border border-brand-line rounded-sm p-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <Icon size={16} weight="duotone" className="text-brand-inkSoft shrink-0" />
                                            <div className="min-w-0">
                                                <p className="text-sm text-brand-ink font-mono truncate">{v.registration_number}</p>
                                                <p className="text-xs text-brand-inkSoft">{[t?.label, v.make, v.model].filter(Boolean).join(" · ")}</p>
                                            </div>
                                        </div>
                                        {isOwnOrUnclaimed && (
                                            <div className="flex items-center gap-1 shrink-0">
                                                <button data-testid={`edit-vehicle-${v.id}`} onClick={() => openEditVehicle(v)} className="p-1.5 hover:bg-brand-sage rounded-sm"><PencilSimple size={13} /></button>
                                                <button data-testid={`deactivate-vehicle-${v.id}`} onClick={() => deactivateVehicle(v)} className="p-1.5 hover:bg-[#FEF2F2] rounded-sm text-brand-action"><Trash size={13} /></button>
                                            </div>
                                        )}
                                    </div>
                                    <div data-testid={`vehicle-approval-status-${v.id}`}>
                                        <Chip variant={APPROVAL_STATUS_VARIANTS[approvalStatus] || "default"}>{APPROVAL_STATUS_LABELS[approvalStatus] || approvalStatus}</Chip>
                                    </div>
                                    {approvalStatus === "rejected" && v.rejection_reason && (
                                        <p data-testid={`vehicle-rejection-reason-${v.id}`} className="text-xs text-[#DC2626] bg-[#FEF2F2] border border-[#FECACA] rounded-md px-2 py-1">
                                            Reason: {v.rejection_reason}
                                        </p>
                                    )}
                                    {approvalStatus === "pending" && (
                                        <p className="text-xs text-brand-inkSoft">Awaiting admin/committee approval before it can be parked.</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Appliances & AMC */}
            <div className="mb-10">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h2 className="font-heading text-xl text-brand-ink">Appliances</h2>
                        <p className="text-sm text-brand-inkSoft">Track purchase, warranty and AMC details for your home appliances.</p>
                    </div>
                    <Button data-testid="add-appliance-btn" onClick={openAddAppliance} size="sm"
                        className="rounded-full bg-brand-action hover:bg-brand-actionHover text-white">
                        <Plus size={14} className="mr-1" /> Add appliance
                    </Button>
                </div>
                {appliances.length === 0 ? (
                    <EmptyState title="No appliances added yet" description="Add an appliance manually, or upload a purchase invoice and let Maintayn read the details for you." />
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {appliances.map((a) => {
                            const t = APPLIANCE_TYPE_MAP[a.appliance_type] || APPLIANCE_TYPE_MAP.other;
                            const Icon = t.icon;
                            return (
                                <div key={a.id} data-testid={`appliance-card-${a.id}`}
                                    className="bg-white border border-brand-line rounded-sm p-4 hover:-translate-y-1 hover:shadow-lg transition-transform duration-200">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-10 h-10 rounded-sm flex items-center justify-center shrink-0" style={{ background: t.color + "22", color: t.color }}>
                                                <Icon size={20} weight="duotone" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-brand-ink truncate">{a.brand || t.label} {a.model || ""}</p>
                                                <p className="text-[10px] uppercase tracking-overline text-brand-inkSoft">{a.appliance_type === "other" && a.other_type_label ? a.other_type_label : t.label}</p>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <button data-testid={`edit-appliance-${a.id}`} onClick={() => openEditAppliance(a)} className="p-1.5 hover:bg-brand-sage rounded-sm"><PencilSimple size={13} /></button>
                                            <button data-testid={`delete-appliance-${a.id}`} onClick={() => removeAppliance(a)} className="p-1.5 hover:bg-[#FEF2F2] rounded-sm text-brand-action"><Trash size={13} /></button>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 mt-3">
                                        <Chip variant={a.warranty_status === "active" ? "success" : a.warranty_status === "expired" ? "danger" : "neutral"}>
                                            Warranty {a.warranty_status === "active" ? "Active" : a.warranty_status === "expired" ? "Expired" : "Unknown"}
                                        </Chip>
                                        {a.amc_status !== "none" && (
                                            <Chip variant={a.amc_status === "active" ? "success" : "danger"}>AMC {a.amc_status === "active" ? "Active" : "Expired"}</Chip>
                                        )}
                                    </div>
                                    {a.invoice_attached && (
                                        <p className="text-xs text-brand-inkSoft mt-2 flex items-center gap-1"><FilePdf size={12} weight="duotone" /> Purchase invoice attached</p>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Bills */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h2 className="font-heading text-xl text-brand-ink">Utility bills</h2>
                </div>
                {bills.length === 0 ? (
                    <EmptyState title="No bills logged yet" description="Log your first electricity, gas, or water bill to start tracking." />
                ) : (
                    <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-brand-bg border-b border-brand-line text-left">
                                    {["Utility", "Provider", "Customer ID", "Period", "Amount", "Due", "Status", ""].map(h => (
                                        <th key={h} className="py-3 px-4 font-medium text-brand-inkSoft text-[10px] uppercase tracking-overline">{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {bills.map((b) => {
                                    const u = UTIL_MAP[b.utility_type] || UTIL_MAP.other;
                                    const Icon = u.icon;
                                    return (
                                        <tr key={b.id} data-testid={`utility-bill-${b.id}`} className="border-b border-brand-line/60 hover:bg-brand-bg/50">
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2">
                                                    <Icon size={16} weight="duotone" style={{ color: u.color }} />
                                                    <span className="font-medium text-brand-ink">{u.label}</span>
                                                </div>
                                            </td>
                                            <td className="py-3 px-4 text-brand-inkSoft">{b.provider_name || "—"}</td>
                                            <td className="py-3 px-4 text-brand-inkSoft font-mono text-xs">{b.customer_id || "—"}</td>
                                            <td className="py-3 px-4 text-brand-inkSoft">{b.bill_period}</td>
                                            <td className="py-3 px-4 font-heading text-brand-ink">{inr(b.amount)}</td>
                                            <td className="py-3 px-4 text-brand-inkSoft">{b.due_date}</td>
                                            <td className="py-3 px-4"><Chip variant={b.status === "paid" ? "success" : "warn"}>{b.status}</Chip></td>
                                            <td className="py-3 px-4 text-right">
                                                {b.status !== "paid" && (
                                                    <button data-testid={`pay-utility-bill-${b.id}`} onClick={() => payBill(b)}
                                                        className="text-brand-action hover:underline text-xs font-medium inline-flex items-center gap-1">
                                                        <CheckCircle size={12} /> Mark paid
                                                    </button>
                                                )}
                                                <button data-testid={`delete-utility-bill-${b.id}`} onClick={() => removeBill(b)}
                                                    className="ml-3 p-1.5 hover:bg-[#FEF2F2] rounded-sm text-brand-action inline-flex">
                                                    <Trash size={13} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Connection Dialog */}
            <Dialog open={connOpen} onOpenChange={setConnOpen}>
                <DialogContent className="rounded-sm">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-2xl tracking-tight">
                            {editingConn ? "Edit connection" : "Add utility connection"}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={saveConn} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Utility</Label>
                            <Select value={connForm.utility_type}
                                onValueChange={(v) => setConnForm({ ...connForm, utility_type: v, biller_id: "", provider_name: "" })}>
                                <SelectTrigger data-testid="conn-utility-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                <SelectContent>{UTILITIES.map(u => <SelectItem key={u.key} value={u.key}>{u.label}</SelectItem>)}</SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Biller</Label>
                            <Select value={connForm.biller_id || "__other"}
                                onValueChange={(v) => setConnForm({ ...connForm, biller_id: v === "__other" ? "" : v, provider_name: v === "__other" ? connForm.provider_name : "" })}>
                                <SelectTrigger data-testid="conn-biller-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {billerOptions.map((b) => <SelectItem key={b.id} value={b.id}>{b.display_name || b.name}</SelectItem>)}
                                    <SelectItem value="__other">Other / not listed</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-brand-inkSoft">
                                {billerOptions.length ? "Pick your biller, or choose \"Other\" to enter it manually." : "No pre-listed billers for this utility yet — enter one manually."}
                            </p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            {!connForm.biller_id && (
                                <div className="space-y-2">
                                    <Label>Service provider</Label>
                                    <Input data-testid="conn-provider-input" required value={connForm.provider_name}
                                        onChange={(e) => setConnForm({ ...connForm, provider_name: e.target.value })}
                                        placeholder="BESCOM, Adani Gas..."
                                        className="rounded-sm border-brand-line" />
                                </div>
                            )}
                            <div className="space-y-2">
                                <Label>{selectedBiller?.customer_id_label || "Customer ID"}</Label>
                                <Input data-testid="conn-customer-input" required value={connForm.customer_id}
                                    onChange={(e) => setConnForm({ ...connForm, customer_id: e.target.value })}
                                    placeholder="e.g. 5000123456"
                                    className="rounded-sm border-brand-line font-mono" />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Nickname (optional)</Label>
                                <Input data-testid="conn-nickname-input" value={connForm.nickname}
                                    onChange={(e) => setConnForm({ ...connForm, nickname: e.target.value })}
                                    placeholder="e.g. Home Wi-Fi"
                                    className="rounded-sm border-brand-line" />
                            </div>
                            <div className="space-y-2">
                                <Label>Meter / Account no. (optional)</Label>
                                <Input data-testid="conn-meter-input" value={connForm.meter_number}
                                    onChange={(e) => setConnForm({ ...connForm, meter_number: e.target.value })}
                                    className="rounded-sm border-brand-line" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Notes (optional)</Label>
                            <Textarea rows={2} value={connForm.notes} onChange={(e) => setConnForm({ ...connForm, notes: e.target.value })}
                                className="rounded-sm border-brand-line" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setConnOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" data-testid="conn-submit-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">
                                {editingConn ? "Save" : "Add"}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Bill Dialog */}
            <Dialog open={billOpen} onOpenChange={setBillOpen}>
                <DialogContent className="rounded-sm">
                    <DialogHeader><DialogTitle className="font-heading text-2xl tracking-tight">Log utility bill</DialogTitle></DialogHeader>
                    <form onSubmit={saveBill} className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Utility</Label>
                                <Select value={billForm.utility_type} onValueChange={(v) => setBillForm({ ...billForm, utility_type: v })}>
                                    <SelectTrigger data-testid="bill-utility-select" className="rounded-sm border-brand-line"><SelectValue /></SelectTrigger>
                                    <SelectContent>{UTILITIES.map(u => <SelectItem key={u.key} value={u.key}>{u.label}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <Label>Connection</Label>
                                <Select value={billForm.connection_id || "__none"}
                                    onValueChange={(v) => v === "__none" ? setBillForm({ ...billForm, connection_id: "" }) : onPickConnection(v)}>
                                    <SelectTrigger data-testid="bill-conn-select" className="rounded-sm border-brand-line"><SelectValue placeholder="None" /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="__none">None (fill manually)</SelectItem>
                                        {conns.map((c) => (
                                            <SelectItem key={c.id} value={c.id}>
                                                {UTIL_MAP[c.utility_type]?.label} · {c.provider_name} · {c.customer_id}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label>Provider</Label>
                                <Input value={billForm.provider_name} onChange={(e) => setBillForm({ ...billForm, provider_name: e.target.value })}
                                    className="rounded-sm border-brand-line" />
                            </div>
                            <div className="space-y-2">
                                <Label>Customer ID</Label>
                                <Input value={billForm.customer_id} onChange={(e) => setBillForm({ ...billForm, customer_id: e.target.value })}
                                    className="rounded-sm border-brand-line font-mono" />
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3">
                            <div className="space-y-2">
                                <Label>Amount (₹)</Label>
                                <Input data-testid="bill-amount-input" required type="number" step="0.01" value={billForm.amount}
                                    onChange={(e) => setBillForm({ ...billForm, amount: e.target.value })}
                                    className="rounded-sm border-brand-line" />
                            </div>
                            <div className="space-y-2">
                                <Label>Bill period</Label>
                                <Input data-testid="bill-period-input" required value={billForm.bill_period}
                                    onChange={(e) => setBillForm({ ...billForm, bill_period: e.target.value })}
                                    placeholder="2026-01" className="rounded-sm border-brand-line" />
                            </div>
                            <div className="space-y-2">
                                <Label>Due date</Label>
                                <Input data-testid="bill-due-input" required type="date" value={billForm.due_date}
                                    onChange={(e) => setBillForm({ ...billForm, due_date: e.target.value })}
                                    className="rounded-sm border-brand-line" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Notes</Label>
                            <Textarea rows={2} value={billForm.notes} onChange={(e) => setBillForm({ ...billForm, notes: e.target.value })}
                                className="rounded-sm border-brand-line" />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setBillOpen(false)} className="rounded-full">Cancel</Button>
                            <Button type="submit" data-testid="bill-submit-btn" className="rounded-full bg-brand-action hover:bg-brand-actionHover">Save</Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            <AddApplianceDialog open={applianceOpen} onOpenChange={setApplianceOpen} flatId={flat.id}
                editing={editingAppliance} onSaved={() => loadAppliances(flat.id)} />
            <VehicleDialog open={vehicleOpen} onOpenChange={setVehicleOpen} flatId={flat.id}
                editing={editingVehicle} onSaved={() => loadVehicles(flat.id)} />
        </div>
    );
}
